import React, { useState, useEffect, useCallback } from "react";
import {
  Box, Typography, Tabs, Tab, Grid, Paper, Chip, Button,
  IconButton, Tooltip, TextField, Select, MenuItem, FormControl, InputLabel
} from "@mui/material";
import {
  Visibility as ViewIcon, FilterList as FilterIcon, FileDownload as ExportIcon
} from "@mui/icons-material";
import { fetchData } from "../../BaseUrl";
import Dynamictablepagination from "../../DynamicComponent/Dynamictablepagination";
import Toast from "../../DynamicComponent/Toast";
import ComplaintDetailDialog from "./ComplaintDetailDialog";

const TAB_LABELS = ["All", "Student Complaint", "Teacher Complaint"];

const statusColorMap = {
  "Assigned to Committee": { color: "#336599", bg: "#f3e5f5" },
  "Under Investigation": { color: "#ff9800", bg: "#fff3e0" },
  "New": { color: "#f44336", bg: "#fce4ec" },
  "Resolved": { color: "#4caf50", bg: "#e8f5e9" },
  "Rejected": { color: "#616161", bg: "#f5f5f5" }
};

const AdminComplaints = () => {
  const [tabValue, setTabValue] = useState(0);
  const [complaints, setComplaints] = useState([]);
  const [pagination, setPagination] = useState({ total: 0, page: 1, totalPages: 1 });
  const [filters, setFilters] = useState({ search: "", status: "" });
  const [selectedComplaint, setSelectedComplaint] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [stats, setStats] = useState({
    total: 18, underInvestigation: 5, resolved: 10, rejected: 3
  });
  const [toast, setToast] = useState({ open: false, severity: "success", message: "" });

  const loadComplaints = useCallback(async (page = 1) => {
    try {
      const params = new URLSearchParams({ page, limit: 10 });
      if (filters.search) params.append("search", filters.search);
      if (filters.status) params.append("status", filters.status);
      const tabTypes = ["all", "student", "teacher"];
      if (tabValue > 0) params.append("type", tabTypes[tabValue]);

      const result = await fetchData(`admin/complaint/list?${params.toString()}`);
      if (result?.data) {
        setComplaints(result.data.complaints || result.data || []);
        setPagination(result.data.pagination || { total: 0, page: 1, totalPages: 1 });
        if (result.data.stats) setStats(result.data.stats);
      }
    } catch (err) {
      setToast({ open: true, severity: "error", message: "Failed to load complaints" });
    }
  }, [tabValue, filters]);

  useEffect(() => { loadComplaints(); }, [loadComplaints]);

  const handleActionComplete = (message) => {
    setToast({ open: true, severity: "success", message });
    setDetailOpen(false);
    loadComplaints(pagination.page);
  };

  const kpiCards = [
    { label: "Total Complaints", value: stats.total, color: "#244C77", bg: "#EAF1F8" },
    { label: "Under Investigation", value: stats.underInvestigation, color: "#ff9800", bg: "#fff3e0" },
    { label: "Resolved", value: stats.resolved, color: "#4caf50", bg: "#e8f5e9" },
    { label: "Rejected", value: stats.rejected, color: "#f44336", bg: "#fce4ec" }
  ];

  const columns = [
    { label: "Complaint ID", field: "complaintId" },
    { label: "Complainant", field: "complainant" },
    { label: "Subject", field: "subject" },
    { label: "Date", field: "date" },
    { label: "Committee", field: "committee" },
    { label: "Status", field: "statusChip" },
    { label: "Action", field: "actions" }
  ];

  const rows = complaints.map((c) => {
    const st = statusColorMap[c.status] || statusColorMap["New"];
    return {
      ...c,
      complaintId: c.complaintId || c.id || "-",
      complainant: c.complainant || c.complainantName || "-",
      subject: c.subject || c.title || "-",
      date: c.date || c.filedDate || c.createdAt?.slice(0, 10) || "-",
      committee: c.committee || c.assignedCommittee || "-",
      statusChip: (
        <Chip label={c.status || "New"} size="small"
          sx={{ backgroundColor: st.bg, color: st.color, fontWeight: 600 }} />
      ),
      actions: (
        <Tooltip title="View">
          <IconButton size="small" onClick={() => { setSelectedComplaint(c); setDetailOpen(true); }}>
            <ViewIcon fontSize="small" sx={{ color: "#244C77" }} />
          </IconButton>
        </Tooltip>
      )
    };
  });

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="body2" color="text.secondary" mb={1}>Pages / Complaint Management</Typography>
      <Typography variant="h5" fontWeight="bold" mb={3}>Complaint Management</Typography>

      {/* KPI Cards */}
      <Grid container spacing={2} mb={3}>
        {kpiCards.map((kpi, idx) => (
          <Grid item xs={6} sm={3} key={idx}>
            <Paper sx={{ p: 2, textAlign: "center", borderRadius: 2, backgroundColor: kpi.bg }}>
              <Typography variant="h4" fontWeight="bold" color={kpi.color}>{kpi.value}</Typography>
              <Typography variant="body2" color="text.secondary">{kpi.label}</Typography>
            </Paper>
          </Grid>
        ))}
      </Grid>

      {/* Filters */}
      <Grid container spacing={2} mb={2} alignItems="center">
        <Grid item xs={12} sm={3}>
          <TextField size="small" fullWidth placeholder="Search complaints..."
            value={filters.search} onChange={(e) => setFilters(p => ({ ...p, search: e.target.value }))}
            onKeyDown={(e) => e.key === "Enter" && loadComplaints()} />
        </Grid>
        <Grid item xs={6} sm={2}>
          <FormControl fullWidth size="small">
            <InputLabel>Status</InputLabel>
            <Select value={filters.status} label="Status"
              onChange={(e) => setFilters(p => ({ ...p, status: e.target.value }))}>
              <MenuItem value="">All</MenuItem>
              <MenuItem value="New">New</MenuItem>
              <MenuItem value="Assigned to Committee">Assigned to Committee</MenuItem>
              <MenuItem value="Under Investigation">Under Investigation</MenuItem>
              <MenuItem value="Resolved">Resolved</MenuItem>
              <MenuItem value="Rejected">Rejected</MenuItem>
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={6} sm={2}>
          <Button variant="outlined" startIcon={<FilterIcon />} onClick={() => loadComplaints()}
            sx={{ height: 40 }}>Apply</Button>
        </Grid>
        <Grid item xs={12} sm={5} sx={{ display: "flex", justifyContent: "flex-end" }}>
          <Button variant="outlined" startIcon={<ExportIcon />}
            sx={{ textTransform: "none" }}>Export</Button>
        </Grid>
      </Grid>

      {/* Tabs */}
      <Tabs value={tabValue} onChange={(_, v) => setTabValue(v)}
        sx={{ mb: 3, borderBottom: 1, borderColor: "divider" }}>
        {TAB_LABELS.map((label) => <Tab key={label} label={label} />)}
      </Tabs>

      <Dynamictablepagination columns={columns} rows={rows} page={pagination?.page || 1} totalPages={pagination?.totalPages || 1} handleChangePage={(e, newPage) => loadComplaints(newPage)} />

      {selectedComplaint && (
        <ComplaintDetailDialog open={detailOpen} onClose={() => setDetailOpen(false)}
          complaint={selectedComplaint} onSuccess={handleActionComplete} />
      )}

      <Toast open={toast.open} handleClose={() => setToast({ ...toast, open: false })}
        severity={toast.severity} message={toast.message} />
    </Box>
  );
};

export default AdminComplaints;
